import { useHotkeys } from "@mantine/hooks";
import { Button } from "@win-reality/react-ui";
import { useDispatch, useSelector } from "react-redux";

import { setTaggingFlowStep } from "../../../../store/slices/hotkeys";
import {
  putTag,
  removeTag,
  setTaggingState,
} from "../../../../store/slices/session";
import type { RootState } from "../../../../store/store";

/**
 * RunsScoredStep - Select number of runs scored (0-4)
 *
 * This is the FINAL step - does NOT call incrementTaggingFlowStep
 *
 * Options:
 * - 0 (0), 1 (1), 4 (4)
 * - 2 (2), 3 (3)
 */
export const RunsScoredStep = () => {
  const tagData = useSelector((state: RootState) => state.session.tagData);
  const dispatch = useDispatch();
  const tagKey = "runsScored";

  function handleTag(value: number) {
    dispatch(setTaggingState(true));
    if (tagData[tagKey] === value) {
      dispatch(removeTag({ key: tagKey }));
    } else {
      dispatch(putTag({ key: tagKey, value }));
      // NOTE: This is the last step - do NOT call incrementTaggingFlowStep
    }
  }

  const hotkeys = {
    "0": "0",
    "1": "1",
    "2": "2",
    "3": "3",
    "4": "4",
  };

  useHotkeys([
    [hotkeys[0], () => handleTag(0)],
    [hotkeys[1], () => handleTag(1)],
    [hotkeys[2], () => handleTag(2)],
    [hotkeys[3], () => handleTag(3)],
    [hotkeys[4], () => handleTag(4)],
  ]);

  const isCompleted = [0, 1, 2, 3, 4].includes(tagData.runsScored);

  return (
    <div className="flex w-full flex-col items-stretch gap-0">
      {/* Previously completed tags - clickable to jump back */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(0))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.prePitchStance ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(1))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.pitchType ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(2))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.pitchResult ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(3))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.playResult ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(4))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.contactType ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(5))}
      >
        <p className="text-sm font-bold text-blue">
          {[0, 1, 2, 3].includes(tagData.outsRecorded)
            ? `${tagData.outsRecorded} out`
            : "---"}
        </p>
      </div>

      {/* Instructions or Completed Value */}
      {!isCompleted ? (
        <div className="flex h-10 items-center justify-center">
          <p className="text-sm italic text-text-light">
            How many runs were scored?
          </p>
        </div>
      ) : (
        <div className="flex h-10 items-center justify-center">
          <p className="text-sm font-bold text-blue">
            {tagData.runsScored} runs
          </p>
        </div>
      )}

      {/* Button Grid - 3 columns (2+2+1) */}
      <div className="flex w-full items-start gap-0">
        {/* Left Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === 0 ? "blue" : "ghost"}
              onClick={() => handleTag(0)}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                0
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys[0]}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === 2 ? "blue" : "ghost"}
              onClick={() => handleTag(2)}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                2
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys[2]}
                </kbd>
              </span>
            </Button>
          </div>
        </div>

        {/* Middle Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === 1 ? "blue" : "ghost"}
              onClick={() => handleTag(1)}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                1
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys[1]}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === 3 ? "blue" : "ghost"}
              onClick={() => handleTag(3)}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                3
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys[3]}
                </kbd>
              </span>
            </Button>
          </div>
        </div>

        {/* Right Column - single button */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === 4 ? "blue" : "ghost"}
              onClick={() => handleTag(4)}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                4
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys[4]}
                </kbd>
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
